import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ActionModal from 'asterX-UI/Modals/ActionModal'
import { FutureIconContainer, LockedIcon } from 'asterX-UI/Icons'
import { Checkbox, FormControlLabel, Grid } from '@material-ui/core'
import { LoaderBackdrop } from 'asterX-UI/Loaders'
import { useHomeStore } from 'context/HomeStore'
import { AutoCompleteField } from 'asterX-UI/Inputs'
import { AuthService } from 'lib/api'
import { useResErrors, useStateCallback } from 'hooks'
import { useProfileStore } from 'context/ProfileStore'

const PROJECT = 'project'
const BRANCH_LIST = 'branchList'
const ROLE = 'role'

const namespace = 'profilePage.accessInTheSystem.addEditAccessModal.'
const AddEditAccessModal = ({
  open = false,
  onClose = () => {},
  tempAccess = {},
  branchCode
}) => {
  const initialState = {
    [ROLE]: null,
    [PROJECT]: null,
    [BRANCH_LIST]: [branchCode]
  }
  const {
    homeStore: { dictionary }
  } = useHomeStore()
  const {
    profileStore: { user = {} },
    profileStoreFunc: { addNewRole, removeRole }
  } = useProfileStore()
  const { t } = useTranslation()
  const { standardCheck } = useResErrors()
  const _isMounted = useRef(true)
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState({
    init: false,
    save: false
  })
  const [values, setValues] = useStateCallback(initialState)
  const isEdit = !!tempAccess?.id
  useEffect(() => {
    if (isEdit) {
      const { id, code, name, project, branchList } = tempAccess
      setValues(prevState => ({
        ...prevState,
        [ROLE]: { id, code, name },
        project,
        branchList
      }))
      getRoles(project?.code)
    }
    return () => {
      _isMounted.current = false
    }
  }, [])

  const setLoader = (key, value) =>
    setLoading(prevState => ({ ...prevState, [key]: value }))

  const onChange = e => {
    const { name, value } = e.target
    setValues(
      prevState => ({
        ...prevState,
        [name]: value
      }),
      state => {
        if (name === PROJECT) {
          getRoles(state?.[PROJECT]?.code)
        }
      }
    )
  }

  const getRoles = async projectCode => {
    setLoader('init', true)
    await AuthService.getRolesProject(projectCode).then(res =>
      standardCheck(
        res,
        () => {
          setLoader('init', false)
          setRoles(res.data || [])
        },
        {
          onFail: () => {
            setLoader('init', false)
          },
          textErr: t(namespace + 'errors.roles')
        }
      )
    )
  }

  const handleSave = async () => {
    const { role, project, branchList } = values
    if (isEdit && user?.id) {
      setLoader('save', true)
      return await AuthService.deleteUserRole({
        userId: user?.id,
        roleId: tempAccess?.id
      }).then(res =>
        standardCheck(
          res,
          async () => {
            removeRole(tempAccess.id)
            await addAccess()
          },
          {
            onFail: () => setLoader('save', false),
            textErr: t(namespace + 'errors.delete')
          }
        )
      )
    }

    if (user?.id) {
      setLoader('save', true)
      return await addAccess()
    }

    if (isEdit) {
      removeRole(tempAccess.id)
    }
    addNewRole({
      ...role,
      project,
      branchList
    })
    onClose()
  }

  const addAccess = async () => {
    const { role, project, branchList } = values
    await AuthService.addUserRole({
      userId: user.id,
      roleId: role?.id,
      branchList
    }).then(res =>
      standardCheck(
        res,
        () => {
          setLoader('save', false)
          addNewRole({
            ...role,
            project,
            branchList
          })
          onClose()
        },
        {
          onFail: () => {
            setLoader('save', false)
          },
          textErr: t(namespace + 'errors.save')
        }
      )
    )
  }

  const userProjects =
    user?.roles?.length > 0 ? user?.roles?.map(item => item?.project?.code) : []
  const projects =
    user?.roles?.length > 0
      ? dictionary?.projects?.filter(prj => !userProjects.includes(prj?.code))
      : dictionary?.projects || []

  const selectAllBranches = e => {
    const { checked } = e.target
    setValues(prevState => ({
      ...prevState,
      [BRANCH_LIST]: checked
        ? dictionary?.branches?.map(v => v?.code) || []
        : []
    }))
  }
  return (
    <ActionModal
      open={open}
      color={'primary'}
      onClose={onClose}
      onCancel={onClose}
      onConfirm={handleSave}
      iconComponent={
        <FutureIconContainer color={'primary'}>
          <LockedIcon />
        </FutureIconContainer>
      }
      title={t(namespace + (isEdit ? 'titleEdit' : 'title'), {
        module: values?.[PROJECT]?.name
      })}
      subtitle={t(namespace + 'subtitle')}
      labelConfirm={t(namespace + 'save')}
    >
      <Grid container spacing={2}>
        <LoaderBackdrop loading={loading.save} />
        {!tempAccess?.id && (
          <Grid item xs={12} md={12} lg={12} xl={12}>
            <AutoCompleteField
              name={PROJECT}
              value={values?.[PROJECT]}
              options={projects}
              onChange={onChange}
              valueKeys={['code', 'name']}
              label={t(namespace + PROJECT)}
              placeholder={t(namespace + PROJECT)}
              fullWidth
              disabled={loading.init}
            />
          </Grid>
        )}
        <Grid item xs={12} md={12} lg={12} xl={12}>
          <AutoCompleteField
            name={ROLE}
            value={values?.[ROLE]}
            options={roles}
            onChange={onChange}
            valueKeys={['id', 'code', 'name']}
            label={t(namespace + ROLE)}
            placeholder={t(namespace + ROLE)}
            fullWidth
            loading={loading.init}
            disabled={loading.init || roles.length === 0}
          />
        </Grid>
        <Grid item xs={12} md={12} lg={12} xl={12}>
          <AutoCompleteField
            name={BRANCH_LIST}
            value={values?.[BRANCH_LIST]}
            options={dictionary.branches}
            onChange={onChange}
            valueKey={'code'}
            label={t(namespace + BRANCH_LIST)}
            placeholder={t(namespace + BRANCH_LIST)}
            multiple
            fullWidth
            disabled={loading.init}
          />
        </Grid>
        <Grid item xs={12} md={12} lg={12} xl={12}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                name={'isSystemUser'}
                checked={
                  values?.[BRANCH_LIST]?.length === dictionary?.branches?.length
                }
                onChange={selectAllBranches}
              />
            }
            disabled={loading.init}
            label={t(namespace + 'selectAllBranches')}
            labelPlacement="end"
          />
        </Grid>
      </Grid>
    </ActionModal>
  )
}
export default AddEditAccessModal
